import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import AcupunctureCardService from "../services/AcupunctureCardService";
import PatientService from "../services/PatientService";

function Section({ title, children }) {
  return (
    <div className="card mb-3">
      <div className="card-header bg-primary text-white fw-bold">{title}</div>
      <div className="card-body">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3">
      <label className="form-label fw-semibold">{label}</label>
      {children}
    </div>
  );
}

function SelectField({ label, value, options, onChange }) {
  return (
    <Field label={label}>
      <select className="form-select" value={value || ""} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">—</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </Field>
  );
}

function YesNoField({ label, value, onChange }) {
  return (
    <Field label={label}>
      <select
        className="form-select"
        value={value === true ? "Da" : value === false ? "Ne" : ""}
        onChange={(e) => onChange(e.target.value === "Da" ? true : e.target.value === "Ne" ? false : null)}
      >
        <option value="">—</option>
        <option value="Da">Da</option>
        <option value="Ne">Ne</option>
      </select>
    </Field>
  );
}

function CheckboxField({ label, values, options, onChange }) {
  const list = values || [];
  const toggle = (opt) => {
    if (list.includes(opt)) onChange(list.filter((v) => v !== opt));
    else onChange([...list, opt]);
  };
  return (
    <Field label={label}>
      <div className="d-flex flex-wrap gap-3">
        {options.map((opt) => (
          <div className="form-check" key={opt}>
            <input
              className="form-check-input"
              type="checkbox"
              checked={list.includes(opt)}
              onChange={() => toggle(opt)}
              id={`${label}-${opt}`}
            />
            <label className="form-check-label" htmlFor={`${label}-${opt}`}>{opt}</label>
          </div>
        ))}
      </div>
    </Field>
  );
}

const emptyCard = {
  patientDTO: null,
  examDate: "", workplace: "", occupation: "", address: "",
  allopathicDiagnosis: "", traditionalDiagnosis: "",
  personalHistory: "", familyHistory: "",
  painAreas: [], bloodPressure: null, sleep: null, dreams: null,
  dizziness: null, heartPalpitations: null, sweating: null, sweatingTime: null,
  urinationColor: null, urinationFrequency: null, stool: null, prefers: null, aversion: null,
  edema: null, hearing: null, eyes: [], menstruation: null, pulseDepth: null, pulseSpeed: null,
  cough: null, expectoration: null, tinnitus: null, appetite: null, vomiting: null,
  bloodColor: null, bloodClots: null, tongueColor: null, tongueCoating: null,
  backShuSensitive: null, backShuWhich: "", muFrontSensitive: null, muFrontWhich: "",
  skin: null, hair: [], nails: null,
  exogenousPathogenicFactors: [], endogenousPathogenicFactors: [],
  exteriorInterior: null, coldHeat: null, excessDeficiency: null, yinYang: null,
  affectedMeridians: [], therapyMethods: [], zangFuOrgansAffected: null,
  therapyPrinciple: "", pointSelection: "", sessions: [],
};

export default function AcupunctureCardFormSimple() {
  const { patientID, cardID } = useParams();
  const navigate = useNavigate();
  const pdfRef = useRef();
  const [patient, setPatient] = useState(null);
  const [card, setCard] = useState(emptyCard);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isEdit = Boolean(cardID);

  useEffect(() => {
    if (isEdit) {
      AcupunctureCardService.getById(cardID).then((data) => {
        setCard({ ...emptyCard, ...data });
        setPatient(data.patientDTO);
        setLoading(false);
      });
    } else {
      PatientService.getPatientById(patientID).then((p) => {
        setPatient(p);
        setCard({ ...emptyCard, patientDTO: p });
        setLoading(false);
      });
    }
  }, [cardID, patientID, isEdit]);

  const set = (key) => (value) => setCard((prev) => ({ ...prev, [key]: value }));
  const setInput = (key) => (e) => setCard((prev) => ({ ...prev, [key]: e.target.value }));

  const updateSessionField = (index, key, value) => {
    setCard((prev) => {
      const sessions = [...prev.sessions];
      sessions[index] = { ...sessions[index], [key]: value };
      return { ...prev, sessions };
    });
  };

  const saveSessionField = (session) => {
    AcupunctureCardService.updateSession(session.sessionID, {
      therapy: session.therapy,
      patientState: session.patientState,
    }).catch((err) => {
      console.error(err);
      alert("Greška pri čuvanju sesije: " + err.message);
    });
  };

  const downloadPDF = async () => {
    const element = pdfRef.current;
    const buttons = document.querySelectorAll(".no-pdf-tools");
    buttons.forEach((b) => (b.style.display = "none"));

    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save(`Karton_akupunktura_${patient?.name || ""}_${patient?.surname || ""}.pdf`);
    } catch (error) {
      console.error("Greška pri generisanju PDF-a:", error);
    } finally {
      buttons.forEach((b) => (b.style.display = ""));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...card, patientDTO: patient };
      if (isEdit) {
        await AcupunctureCardService.update(cardID, payload);
      } else {
        const created = await AcupunctureCardService.create(payload);
        navigate(`/acupuncture-cards/${created.cardID}/edit-simple`, { replace: true });
      }
      alert("Karton je sačuvan.");
    } catch (err) {
      console.error(err);
      alert("Greška pri čuvanju kartona: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: "40px", textAlign: "center" }}>Učitavanje...</div>;

  return (
    <div className="container mt-4 mb-5" style={{ maxWidth: "900px" }}>
      <div className="d-flex justify-content-between align-items-center mb-3 no-pdf-tools">
        <h3>Karton akupunkture {patient ? `— ${patient.name} ${patient.surname}` : ""}</h3>
        <div>
          <button className="btn btn-secondary me-2" onClick={() => navigate(-1)}>← Nazad</button>
          <button className="btn btn-success me-2" onClick={handleSave} disabled={saving}>
            {saving ? "Čuvanje..." : "💾 Sačuvaj"}
          </button>
          <button className="btn btn-warning" onClick={downloadPDF}>📄 Preuzmi PDF</button>
        </div>
      </div>

      <div ref={pdfRef}>
      <Section title="Osnovni podaci">
        <div className="row">
          <div className="col-md-6">
            <Field label="Broj kartona">
              <div className="form-control-plaintext">{isEdit ? card.cardID : "biće dodeljen nakon čuvanja"}</div>
            </Field>
          </div>
          <div className="col-md-6">
            <Field label="Datum pregleda">
              <input type="date" className="form-control" value={card.examDate || ""} onChange={setInput("examDate")} />
            </Field>
          </div>
          <div className="col-md-6">
            <Field label="Ime i prezime"><div className="form-control-plaintext">{patient ? `${patient.name} ${patient.surname}` : "/"}</div></Field>
          </div>
          <div className="col-md-6">
            <Field label="Telefon"><div className="form-control-plaintext">{patient?.phone || "/"}</div></Field>
          </div>
          <div className="col-md-6">
            <Field label="Datum rodjenja"><div className="form-control-plaintext">{patient?.birthDate ? String(patient.birthDate).split("T")[0] : "/"}</div></Field>
          </div>
          <div className="col-md-6">
            <Field label="Radno mesto">
              <input className="form-control" value={card.workplace || ""} onChange={setInput("workplace")} />
            </Field>
          </div>
          <div className="col-md-6">
            <Field label="Zanimanje">
              <input className="form-control" value={card.occupation || ""} onChange={setInput("occupation")} />
            </Field>
          </div>
          <div className="col-md-6">
            <Field label="Adresa">
              <input className="form-control" value={card.address || ""} onChange={setInput("address")} />
            </Field>
          </div>
          <div className="col-md-6">
            <Field label="Alergije"><div className="form-control-plaintext">{patient?.allergies || "Nema"}</div></Field>
          </div>
          <div className="col-md-6">
            <Field label="Operacije"><div className="form-control-plaintext">{patient?.surgeries || "/"}</div></Field>
          </div>
          <div className="col-12">
            <Field label="Hronično"><div className="form-control-plaintext">{patient?.otherInfo || "/"}</div></Field>
          </div>
        </div>
      </Section>

      <Section title="Dijagnoza">
        <Field label="Alopatska">
          <textarea className="form-control" rows={3} value={card.allopathicDiagnosis || ""} onChange={setInput("allopathicDiagnosis")} />
        </Field>
        <Field label="Tradicionalna">
          <textarea className="form-control" rows={3} value={card.traditionalDiagnosis || ""} onChange={setInput("traditionalDiagnosis")} />
        </Field>
      </Section>

      <Section title="Kratka anamneza">
        <Field label="Lična - status">
          <textarea className="form-control" rows={3} value={card.personalHistory || ""} onChange={setInput("personalHistory")} />
        </Field>
        <Field label="Porodična">
          <textarea className="form-control" rows={3} value={card.familyHistory || ""} onChange={setInput("familyHistory")} />
        </Field>
      </Section>

      <Section title="Simptomi">
        <CheckboxField label="Bolovi" values={card.painAreas} onChange={set("painAreas")}
          options={["Glava", "Vrat", "Telo", "Mišići", "Udovi", "Kosti"]} />
        <SelectField label="Krvni pritisak" value={card.bloodPressure} onChange={set("bloodPressure")} options={["Visok", "Normalan", "Nizak"]} />
        <SelectField label="Spavanje" value={card.sleep} onChange={set("sleep")} options={["Dobro", "Loše"]} />
        <SelectField label="Snovi" value={card.dreams} onChange={set("dreams")} options={["Prijatni", "Neprijatni"]} />
        <YesNoField label="Vrtoglavica" value={card.dizziness} onChange={set("dizziness")} />
        <YesNoField label="Srčane palpitacije" value={card.heartPalpitations} onChange={set("heartPalpitations")} />
        <YesNoField label="Znojenje" value={card.sweating} onChange={set("sweating")} />
        <SelectField label="Znojenje - vreme" value={card.sweatingTime} onChange={set("sweatingTime")} options={["Noću", "Danju"]} />
        <SelectField label="Uriniranje - boja" value={card.urinationColor} onChange={set("urinationColor")} options={["Svetlo", "Tamna"]} />
        <SelectField label="Uriniranje - učestalost" value={card.urinationFrequency} onChange={set("urinationFrequency")} options={["Često", "Retko"]} />
        <SelectField label="Stolica" value={card.stool} onChange={set("stool")} options={["Meka", "Tvrda", "BP"]} />
        <SelectField label="Preferira" value={card.prefers} onChange={set("prefers")} options={["Hladno", "Toplo"]} />
        <SelectField label="Averzija" value={card.aversion} onChange={set("aversion")} options={["Na hladno", "Na toplo"]} />
        <YesNoField label="Edemi" value={card.edema} onChange={set("edema")} />
        <SelectField label="Sluh" value={card.hearing} onChange={set("hearing")} options={["Dobar", "Slab"]} />
        <CheckboxField label="Oči" values={card.eyes} onChange={set("eyes")} options={["Crvene", "Suve", "BP"]} />
        <SelectField label="Menstruacija" value={card.menstruation} onChange={set("menstruation")} options={["Redovna", "Neredovna", "BP"]} />
        <SelectField label="Puls - dubina" value={card.pulseDepth} onChange={set("pulseDepth")} options={["Dubok", "Površinski"]} />
        <SelectField label="Puls - brzina" value={card.pulseSpeed} onChange={set("pulseSpeed")} options={["Spor", "Brz"]} />
        <YesNoField label="Kašalj" value={card.cough} onChange={set("cough")} />
        <YesNoField label="Iskašljavanje" value={card.expectoration} onChange={set("expectoration")} />
        <YesNoField label="Zvonjenje u ušima" value={card.tinnitus} onChange={set("tinnitus")} />
        <YesNoField label="Apetit" value={card.appetite} onChange={set("appetite")} />
        <YesNoField label="Povraćanje" value={card.vomiting} onChange={set("vomiting")} />
        <SelectField label="Krv - boja" value={card.bloodColor} onChange={set("bloodColor")} options={["Svetla", "Tamna"]} />
        <YesNoField label="Ugrušci" value={card.bloodClots} onChange={set("bloodClots")} />
        <SelectField label="Jezik - boja" value={card.tongueColor} onChange={set("tongueColor")} options={["Crven", "Bled"]} />
        <SelectField label="Jezik - obloga" value={card.tongueCoating} onChange={set("tongueCoating")} options={["Obložen", "Neobložen"]} />
        <YesNoField label="Back Shu (palpatorno osetljive)" value={card.backShuSensitive} onChange={set("backShuSensitive")} />
        <Field label="Ako da, koje (Back Shu)">
          <input className="form-control" value={card.backShuWhich || ""} onChange={setInput("backShuWhich")} />
        </Field>
        <YesNoField label="Mu Front (palpatorno osetljive)" value={card.muFrontSensitive} onChange={set("muFrontSensitive")} />
        <Field label="Ako da, koje (Mu Front)">
          <input className="form-control" value={card.muFrontWhich || ""} onChange={setInput("muFrontWhich")} />
        </Field>
        <SelectField label="Koža" value={card.skin} onChange={set("skin")} options={["Suva", "Crvena", "BP"]} />
        <CheckboxField label="Kosa" values={card.hair} onChange={set("hair")} options={["Suva", "Masna", "Seda", "Retka", "Gusta"]} />
        <SelectField label="Nokti" value={card.nails} onChange={set("nails")} options={["Deformisani", "BP"]} />
      </Section>

      <Section title="TCM kategorije">
        <CheckboxField label="Egzogeni patogeni faktori" values={card.exogenousPathogenicFactors} onChange={set("exogenousPathogenicFactors")}
          options={["Vetar", "Hladnoća", "Toplota", "Suvoća", "Vlaga"]} />
        <CheckboxField label="Endogeni patogeni faktori" values={card.endogenousPathogenicFactors} onChange={set("endogenousPathogenicFactors")}
          options={["Briga", "Strepnja", "Ljutnja", "Depresija", "Radost", "Žalost", "Strah"]} />
        <SelectField label="Spoljno / Unutrašnje" value={card.exteriorInterior} onChange={set("exteriorInterior")} options={["Spoljno", "Unutrašnje"]} />
        <SelectField label="Hladno / Toplo" value={card.coldHeat} onChange={set("coldHeat")} options={["Hladno", "Toplo"]} />
        <SelectField label="Ekces / Deficit" value={card.excessDeficiency} onChange={set("excessDeficiency")} options={["Ekces", "Deficit"]} />
        <SelectField label="Yin / Yang" value={card.yinYang} onChange={set("yinYang")} options={["Yin", "Yang"]} />
        <CheckboxField label="Zahvaćeni meridijani" values={card.affectedMeridians} onChange={set("affectedMeridians")}
          options={["LU", "LI", "ST", "SP", "HT", "SI", "BL", "KI", "PC", "SJ", "GB", "LR"]} />
        <CheckboxField label="Izabrane metode terapije" values={card.therapyMethods} onChange={set("therapyMethods")}
          options={["Akupunktura", "Moksa", "Ventuze", "Elektroakupunktura", "Masaža"]} />
        <YesNoField label="Zahvaćeni Zang Fu organi" value={card.zangFuOrgansAffected} onChange={set("zangFuOrgansAffected")} />
      </Section>

      <Section title="Terapija">
        <Field label="Princip terapije">
          <textarea className="form-control" rows={3} value={card.therapyPrinciple || ""} onChange={setInput("therapyPrinciple")} />
        </Field>
        <Field label="Izbor tačaka">
          <textarea className="form-control" rows={3} value={card.pointSelection || ""} onChange={setInput("pointSelection")} />
        </Field>

        <table className="table table-bordered align-middle">
          <thead>
            <tr>
              <th style={{ width: "50px" }}>Br.</th>
              <th style={{ width: "150px" }}>Datum</th>
              <th>Terapija</th>
              <th>Stanje pacijenta</th>
            </tr>
          </thead>
          <tbody>
            {(card.sessions || []).length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center text-muted">Nema evidentiranih poseta za ovaj karton.</td>
              </tr>
            ) : (
              card.sessions.map((s, i) => (
                <tr key={s.sessionID ?? i}>
                  <td className="text-center">{i + 1}.</td>
                  <td>{s.sessionDate || "/"}</td>
                  <td>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={s.therapy || ""}
                      onChange={(e) => updateSessionField(i, "therapy", e.target.value)}
                      onBlur={() => saveSessionField(s)}
                    />
                  </td>
                  <td>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={s.patientState || ""}
                      onChange={(e) => updateSessionField(i, "patientState", e.target.value)}
                      onBlur={() => saveSessionField(s)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <p className="text-muted small no-pdf-tools">
          Terapija i stanje pacijenta se čuvaju automatski kad izađete iz polja. Nove sesije (redovi) se dodaju kreiranjem posete "Telesna akupunktura" i povezivanjem sa ovim kartonom u formi Nova poseta.
        </p>
      </Section>
      </div>

      <div className="d-flex justify-content-end no-pdf-tools">
        <button className="btn btn-success" onClick={handleSave} disabled={saving}>
          {saving ? "Čuvanje..." : "💾 Sačuvaj"}
        </button>
      </div>
    </div>
  );
}
